import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Mic, MicOff, Pause, Play, Sparkles } from 'lucide-react';
import api from '../../utils/api';
import EmotionChipPicker from '../EmotionChipPicker';
import {
  emotionToMoodCode,
  emotionToLabel,
  emotionToBg,
  emotionToFg,
  isEmotionInput,
} from '../../utils/emotions';
import { useVoiceCapture } from '../../utils/voiceCapture';
import { aiNudgeAllowed } from '../../utils/aiNudge';

const SAGE = '#7C9A82';
const AMBER = '#D4A574';

// One cap for every take. The server allows 15 minutes; this is the client's
// own limit and it is deliberately not a countdown — CLAUDE.md forbids them,
// and a ring closing on someone mid-sentence is exactly the anxiety that rule
// exists to prevent. The only time pressure shown is one quiet line near the end.
const MAX_MS = 15 * 60 * 1000;
const WARN_AT_MS = MAX_MS - 60 * 1000;
const WARN_PCT = WARN_AT_MS / MAX_MS;

const SECTION_OPTIONS = ['Daily Notes', 'Gratitude', 'Guided Reflections'];
const DEFAULT_SECTION = 'Daily Notes';

// How long the saved card waits for words to arrive before it stops asking.
// Bounded on purpose: this is the only timer the sheet owns, and an unbounded
// one on a screen the user can close is a leak waiting to happen.
const TRANSCRIPT_POLL_MS = 3000;
const TRANSCRIPT_POLL_TRIES = 8;

function formatMs(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function prettyDate(iso) {
  const d = iso ? new Date(`${iso}T00:00:00`) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/**
 * The feeling control. Shows "Not sure" when nothing has been picked, so the
 * sheet never reads as an unanswered question — but a value is only *stored*
 * once the user actually taps it.
 *
 * That distinction is the whole design: null means the picker was never opened,
 * 'not_sure' means it was opened and declined. Identical on screen, honest in
 * the database, and it is what will tell us later whether the pre-filled
 * default is costing us the affect signal the chart runs on.
 */
function FeelingControl({ label, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const picked = isEmotionInput(value);
  const bg = picked ? emotionToBg(value) : null;
  const fg = picked ? emotionToFg(value) : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-center gap-2">
        <span className="text-xs uppercase tracking-[0.14em] text-stone-400 dark:text-stone-500">
          {label}
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-expanded={open}
          aria-label={`${label}: ${picked ? emotionToLabel(value) : 'not sure'}`}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-300 ease-out disabled:opacity-50"
          style={{
            backgroundColor: bg || 'transparent',
            color: fg || SAGE,
            border: picked ? '1px solid transparent' : `1px dashed ${SAGE}66`,
          }}
        >
          {picked ? emotionToLabel(value) : 'Not sure'}
          <ChevronDown
            size={12}
            className="transition-transform duration-300 ease-out motion-reduce:transition-none"
            style={{ transform: open ? 'rotate(180deg)' : 'none' }}
          />
        </button>
      </div>
      {open && (
        <div className="pt-1">
          <EmotionChipPicker
            value={value}
            onChange={(code) => { onChange(code); setOpen(false); }}
            size="sm"
            ariaLabel={label}
          />
        </div>
      )}
    </div>
  );
}

function SectionControl({ section, promptId, prompts, onSection, onPrompt }) {
  const [open, setOpen] = useState(false);
  const current =
    section === 'Guided Reflections' && promptId
      ? prompts.find((p) => p.prompt_id === promptId)?.prompt_text || 'a prompt'
      : section;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-center gap-2">
        <span className="text-xs uppercase tracking-[0.14em] text-stone-400 dark:text-stone-500">
          Filing to
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 transition-colors duration-300 max-w-[22ch]"
        >
          <span className="truncate">{current}</span>
          <ChevronDown
            size={12}
            className="shrink-0 transition-transform duration-300 ease-out motion-reduce:transition-none"
            style={{ transform: open ? 'rotate(180deg)' : 'none' }}
          />
        </button>
      </div>
      {open && (
        <div className="flex flex-wrap justify-center gap-2 pt-1">
          {SECTION_OPTIONS.filter(
            (o) => o !== 'Guided Reflections' || prompts.length > 0
          ).map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => {
                onSection(o);
                if (o !== 'Guided Reflections') { onPrompt(null); setOpen(false); }
              }}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors duration-300 ${
                section === o
                  ? 'text-white'
                  : 'text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800'
              }`}
              style={section === o ? { backgroundColor: SAGE } : undefined}
            >
              {o}
            </button>
          ))}
          {section === 'Guided Reflections' &&
            prompts.map((p) => (
              <button
                key={p.prompt_id}
                type="button"
                onClick={() => { onPrompt(p.prompt_id); setOpen(false); }}
                className={`w-full rounded-2xl px-3.5 py-2.5 text-left text-xs leading-relaxed transition-colors duration-300 ${
                  promptId === p.prompt_id
                    ? 'text-white'
                    : 'text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800'
                }`}
                style={promptId === p.prompt_id ? { backgroundColor: SAGE } : undefined}
              >
                {p.prompt_text}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function LevelMeter({ rms, clipping, prefersReducedMotion }) {
  const bars = 24;
  const level = Math.min(1, (rms || 0) * 8);
  return (
    <div className="flex items-end justify-center gap-1 h-10" aria-hidden="true">
      {Array.from({ length: bars }, (_, i) => {
        const centre = 1 - Math.abs(i - (bars - 1) / 2) / ((bars - 1) / 2);
        const h = Math.max(3, level * 40 * (0.45 + centre * 0.55));
        return (
          <span
            key={i}
            className={prefersReducedMotion ? '' : 'transition-all duration-150 ease-out'}
            style={{
              width: 3,
              height: h,
              borderRadius: 2,
              backgroundColor: clipping ? AMBER : SAGE,
              opacity: 0.35 + centre * 0.65,
            }}
          />
        );
      })}
    </div>
  );
}

function AudioPreview({ blob, durationMs, caption }) {
  const [playing, setPlaying] = useState(false);
  const [url, setUrl] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!blob) return undefined;
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => { URL.revokeObjectURL(u); setUrl(null); };
  }, [blob]);

  return (
    <div className="flex items-center gap-3 p-4 rounded-2xl bg-stone-100/70 dark:bg-stone-800/40">
      <button
        type="button"
        onClick={() => {
          const a = audioRef.current;
          if (!a) return;
          if (playing) a.pause(); else a.play().catch(() => {});
        }}
        aria-label={playing ? 'Pause' : 'Play'}
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-transform duration-300 active:scale-95"
        style={{ backgroundColor: SAGE }}
      >
        {playing
          ? <Pause size={20} fill="currentColor" />
          : <Play size={20} fill="currentColor" style={{ marginLeft: 2 }} />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-stone-700 dark:text-[#F0EDE8] tabular-nums">
          {formatMs(durationMs)}
        </p>
        <p className="text-xs text-stone-400 dark:text-stone-500">{caption}</p>
      </div>
      {url && (
        <audio
          ref={audioRef}
          src={url}
          preload="auto"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
      )}
    </div>
  );
}

/**
 * The saved card — the sheet's last state, and the answer to "where did that go?"
 *
 * Three things are true at different moments and it says exactly the one that
 * is true now. The journal row is created on upload, so "Saved to your journal"
 * is honest immediately; the words are not, so they arrive when they arrive.
 * The date is the entry's own date, never "today": a take recorded from a past
 * day's journal belongs to that day, and saying otherwise would be a lie about
 * the user's own record.
 */
function SavedCard({
  blob,
  durationMs,
  entryDate,
  noteId,
  aiOff,
  onTurnOn,
  onOpenVoice,
  onOpenJournal,
  onRecordAnother,
}) {
  const [transcript, setTranscript] = useState(null);
  const [gaveUp, setGaveUp] = useState(false);

  // One bounded chain, cleared on unmount. The Voice list keeps its own poll;
  // this exists only so the words can land in front of the person who just
  // spoke them, and it stops asking rather than spinning forever.
  useEffect(() => {
    if (!noteId || aiOff) return undefined;
    let cancelled = false;
    let timer = null;
    let tries = 0;

    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get('/api/voice', { params: { limit: 5 } });
        const row = (data?.items || []).find((it) => it.id === noteId);
        const text = row?.accepted_text || row?.raw_transcript || null;
        if (cancelled) return;
        if (text) { setTranscript(text); return; }
        if (row && (row.status === 'empty_speech' || row.status === 'failed')) {
          setGaveUp(true);
          return;
        }
      } catch {
        // A failed poll is not worth telling anyone about: the recording is
        // already saved, and the list will show it either way.
      }
      if (cancelled) return;
      if (tries >= TRANSCRIPT_POLL_TRIES) { setGaveUp(true); return; }
      timer = setTimeout(tick, TRANSCRIPT_POLL_MS);
    };

    timer = setTimeout(tick, TRANSCRIPT_POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [noteId, aiOff]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center gap-3 pt-2">
        <span
          className="flex h-12 w-12 items-center justify-center rounded-full"
          style={{ backgroundColor: `${SAGE}1A`, color: SAGE }}
        >
          <Check size={26} strokeWidth={2.2} />
        </span>
        <div className="text-center">
          <p
            className="text-lg font-semibold tracking-tight text-stone-800 dark:text-[#F0EDE8]"
            aria-live="polite"
          >
            {aiOff ? 'Saved as audio' : 'Saved to your journal'}
          </p>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-0.5">
            {prettyDate(entryDate)}
          </p>
        </div>
      </div>

      <AudioPreview
        blob={blob}
        durationMs={durationMs}
        caption="Tap play to hear it back"
      />

      {aiOff ? (
        <p className="text-sm text-center text-stone-500 dark:text-stone-400 leading-relaxed">
          Turn on AI and we'll write it down for you.
        </p>
      ) : transcript ? (
        <p className="text-[15px] leading-relaxed text-stone-700 dark:text-[#F0EDE8] line-clamp-4">
          {transcript}
        </p>
      ) : (
        <div className="space-y-2" aria-live="polite">
          <div className="h-3 w-4/5 rounded-full bg-stone-200/70 dark:bg-stone-700/60 animate-pulse" />
          <div className="h-3 w-3/5 rounded-full bg-stone-200/70 dark:bg-stone-700/60 animate-pulse" />
          <p className="text-xs text-stone-400 dark:text-stone-500 pt-1">
            {gaveUp
              ? "We'll finish writing it down in the background."
              : 'Writing it down…'}
          </p>
        </div>
      )}

      {aiOff && onTurnOn && (
        <button
          type="button"
          onClick={onTurnOn}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
          style={{ backgroundColor: SAGE }}
        >
          <Sparkles size={15} strokeWidth={1.8} />
          Turn on AI to transcribe
        </button>
      )}

      <div className="space-y-3">
        <button
          type="button"
          onClick={onOpenVoice}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0"
          style={{ backgroundColor: SAGE }}
        >
          Open in Voice
        </button>
        {onOpenJournal && (
          <button
            type="button"
            onClick={onOpenJournal}
            className="w-full text-sm text-stone-600 dark:text-stone-300 underline decoration-dotted underline-offset-4 hover:text-stone-800 dark:hover:text-stone-100 transition-colors duration-300"
          >
            See it in your journal
          </button>
        )}
        <button
          type="button"
          onClick={onRecordAnother}
          className="w-full text-sm text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 transition-colors duration-300"
        >
          Record another
        </button>
      </div>
    </div>
  );
}

function StateScreen({ icon: Icon, title, body, actions }) {
  return (
    <div className="py-6 flex flex-col items-center gap-4 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800 text-stone-400">
        <Icon size={24} strokeWidth={1.5} />
      </span>
      <div className="space-y-1">
        <p className="text-base font-medium text-stone-800 dark:text-[#F0EDE8]">{title}</p>
        {body && (
          <p className="text-sm text-stone-500 dark:text-stone-400 max-w-[32ch] leading-relaxed">
            {body}
          </p>
        )}
      </div>
      {actions}
    </div>
  );
}

/**
 * One capture sheet for every entry point — Home, the journal toolbar and the
 * Voice tab — replacing EchoCapture and VoiceJournalCapture, which differed in
 * ways no user ever asked for: one gated on an emotion before it would record,
 * the other on choosing a section; one capped at 2 minutes, the other at 6; one
 * let you hear the take back, the other uploaded it the instant you stopped.
 *
 * The flow is now: tap, talk, stop, Save. Two taps minimum. Everything else —
 * the feeling, where it files — is optional and never blocks the button.
 *
 * `guidedReflections` (and with it the "Filing to" control) is passed only by
 * the journal, which is the one caller that knows what the user is looking at.
 * A stale target from Home would file a recording against a prompt nobody has
 * on screen.
 */
export function VoiceCapture({
  open,
  onClose,
  entryDate,
  onAskAi,
  onUploaded,
  onOpenVoice,
  onOpenJournal,
  guidedReflections = [],
}) {
  const [phase, setPhase] = useState('idle');
  const [emotionBefore, setEmotionBefore] = useState(null);
  const [emotionAfter, setEmotionAfter] = useState(null);
  const [section, setSection] = useState(DEFAULT_SECTION);
  const [promptId, setPromptId] = useState(null);
  const [pending, setPending] = useState({ blob: null, mime: null, durationMs: 0 });
  const [savedId, setSavedId] = useState(null);
  const [aiOff, setAiOff] = useState(false);
  const [serverError, setServerError] = useState(null);
  const triggerRef = useRef(null);

  const capture = useVoiceCapture({ maxDurationMs: MAX_MS, warnAtPct: WARN_PCT });

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }, []);

  const prompts = useMemo(
    () => guidedReflections.filter((p) => p?.prompt_id && p?.prompt_text),
    [guidedReflections]
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setEmotionBefore(null);
    setEmotionAfter(null);
    setSection(DEFAULT_SECTION);
    setPromptId(null);
    setPending({ blob: null, mime: null, durationMs: 0 });
    setSavedId(null);
    setAiOff(false);
    setServerError(null);
  }, []);

  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      reset();
      capture.reset();
    } else {
      capture.cancel();
      if (triggerRef.current?.focus) {
        try { triggerRef.current.focus(); } catch { /* ignore */ }
      }
    }
    // capture.* are stable refs from useVoiceCapture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Capture lifecycle → phase. A finished take lands in review, never straight
  // in the uploader: hearing it back before it is filed is the whole reason the
  // Echo flow felt trustworthy and the journal one did not.
  useEffect(() => {
    if (capture.state === 'permission-denied') setPhase('permission-denied');
    else if (capture.state === 'empty-speech') setPhase('empty-speech');
    else if (capture.state === 'error') setPhase('error');
    else if (capture.state === 'ready-to-upload' && capture.blob) {
      setPending({
        blob: capture.blob,
        mime: capture.mimeType,
        durationMs: capture.elapsedMs,
      });
      setPhase('review');
    }
    // blob/mimeType/elapsedMs are populated atomically with the state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capture.state]);

  const handleSave = useCallback(async () => {
    if (!pending.blob) return;
    setPhase('uploading');
    setServerError(null);
    try {
      const form = new FormData();
      form.append('audio', pending.blob, `voice-${Date.now()}.webm`);
      form.append('entry_date', entryDate);
      form.append('target_section', section);
      if (section === 'Guided Reflections' && promptId) {
        form.append('target_prompt_id', promptId);
      }
      form.append('duration_ms', Math.round(pending.durationMs).toString());
      form.append('mime_type', pending.mime || 'audio/webm');

      // Emotions ride along only when the user actually picked one. Untouched
      // stays untouched — null in the column, which is a different fact from
      // 'not_sure' and is kept that way deliberately.
      if (isEmotionInput(emotionBefore)) {
        form.append('emotion_before', emotionBefore);
        const derived = emotionToMoodCode(emotionBefore);
        if (derived) form.append('mood_before', derived);
      }
      if (isEmotionInput(emotionAfter)) {
        form.append('emotion_after', emotionAfter);
        const derived = emotionToMoodCode(emotionAfter);
        if (derived) form.append('mood_after', derived);
      }

      const { data } = await api.post('/api/voice/upload', form);
      setSavedId(data?.voiceNoteId || data?.id || null);
      setAiOff(data?.status === 'audio_only');
      setPhase('saved');
      onUploaded?.(data);
    } catch (err) {
      setServerError(err?.response?.data?.error || err?.message || 'Upload failed');
      setPhase('error');
    }
  }, [pending, entryDate, section, promptId, emotionBefore, emotionAfter, onUploaded]);

  const startRecording = useCallback(async () => {
    setPhase('recording');
    await capture.start();
  }, [capture]);

  const recordAnother = useCallback(() => {
    capture.reset();
    reset();
  }, [capture, reset]);

  if (!open) return null;

  const showSections = prompts.length > 0 || section !== DEFAULT_SECTION;
  const nearCap = capture.warningAt80;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-stone-900/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Record a voice note"
      onClick={(e) => { if (e.target === e.currentTarget && phase !== 'recording') onClose?.(); }}
    >
      <div className="w-full sm:max-w-md bg-[#FAFAF9] dark:bg-[#1A1816] rounded-t-3xl sm:rounded-3xl shadow-xl p-6 sm:p-7 space-y-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight text-stone-800 dark:text-[#F0EDE8]">
            {phase === 'saved' ? '' : 'Speak'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 -mr-2 rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors duration-300"
          >
            ✕
          </button>
        </div>

        {phase === 'idle' && (
          <>
            <div className="flex flex-col items-center gap-4 py-4">
              <button
                type="button"
                onClick={startRecording}
                aria-label="Start recording"
                className="flex h-20 w-20 items-center justify-center rounded-full text-white shadow-md transition-transform duration-300 ease-out active:scale-95 motion-reduce:transition-none"
                style={{ backgroundColor: '#38332F' }}
              >
                <Mic size={30} strokeWidth={2} />
              </button>
              <p className="text-sm text-stone-500 dark:text-stone-400">Tap to start</p>
            </div>
            <FeelingControl
              label="Right now"
              value={emotionBefore}
              onChange={setEmotionBefore}
            />
            {showSections && (
              <SectionControl
                section={section}
                promptId={promptId}
                prompts={prompts}
                onSection={setSection}
                onPrompt={setPromptId}
              />
            )}
          </>
        )}

        {phase === 'recording' && (
          <>
            <LevelMeter
              rms={capture.level?.rms}
              clipping={capture.level?.clipping}
              prefersReducedMotion={prefersReducedMotion}
            />
            {/* Elapsed only. No ring, no remaining-time, no percentage. */}
            <p className="text-center text-3xl font-semibold tabular-nums tracking-tight text-stone-800 dark:text-[#F0EDE8]">
              {formatMs(capture.elapsedMs)}
            </p>
            {nearCap && (
              <p className="text-xs text-center" style={{ color: AMBER }} aria-live="polite">
                Getting long — we'll wrap up and save at 15 minutes.
              </p>
            )}
            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={capture.stop}
                aria-label="Stop recording"
                className="flex h-20 w-20 items-center justify-center rounded-full text-white shadow-md transition-transform duration-300 ease-out active:scale-95 motion-reduce:transition-none"
                style={{ backgroundColor: '#B45309' }}
              >
                <span className="block h-6 w-6 rounded-[4px] bg-white" />
              </button>
              <p className="text-sm text-stone-500 dark:text-stone-400">Tap to stop</p>
            </div>
          </>
        )}

        {phase === 'review' && (
          <>
            <AudioPreview
              blob={pending.blob}
              durationMs={pending.durationMs}
              caption="Tap play to hear it back"
            />
            <FeelingControl
              label="And now"
              value={emotionAfter}
              onChange={setEmotionAfter}
            />
            <div className="space-y-3 pt-1">
              {/* Enabled from the first frame. Nothing to fill in first. */}
              <button
                type="button"
                onClick={handleSave}
                className="w-full inline-flex items-center justify-center px-6 py-4 rounded-full font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                style={{ backgroundColor: SAGE }}
              >
                Save
              </button>
              <div className="flex items-center justify-center gap-6 text-sm">
                <button
                  type="button"
                  onClick={recordAnother}
                  className="text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 transition-colors duration-300"
                >
                  Re-record
                </button>
                <button
                  type="button"
                  onClick={() => { capture.cancel(); onClose?.(); }}
                  className="text-rose-500/80 hover:text-rose-600 transition-colors duration-300"
                >
                  Discard
                </button>
              </div>
            </div>
          </>
        )}

        {phase === 'uploading' && (
          <div className="py-10 flex flex-col items-center gap-4">
            <div className="flex items-end gap-1.5 h-8" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className="w-1.5 rounded-full"
                  style={{
                    height: 10 + ((i * 7) % 18),
                    backgroundColor: SAGE,
                    opacity: 0.5 + (i % 3) * 0.2,
                  }}
                />
              ))}
            </div>
            <p className="text-sm text-stone-500 dark:text-stone-400" aria-live="polite">
              Saving…
            </p>
          </div>
        )}

        {phase === 'saved' && (
          <SavedCard
            blob={pending.blob}
            durationMs={pending.durationMs}
            entryDate={entryDate}
            noteId={savedId}
            aiOff={aiOff}
            // The real opt-in dialog, owned by App — never a local flag. The
            // cooldown decides whether we may ask at all; the card still
            // discloses that the take is audio-only either way.
            onTurnOn={onAskAi && aiNudgeAllowed() ? () => { onClose?.(); onAskAi(); } : null}
            onOpenVoice={() => { onClose?.(); onOpenVoice?.(); }}
            onOpenJournal={
              onOpenJournal ? () => { onClose?.(); onOpenJournal(entryDate); } : null
            }
            onRecordAnother={recordAnother}
          />
        )}

        {phase === 'empty-speech' && (
          <StateScreen
            icon={MicOff}
            title="We didn't catch any speech"
            body="Try again a little closer to the mic."
            actions={
              <button
                type="button"
                onClick={recordAnother}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white"
                style={{ backgroundColor: SAGE }}
              >
                Try again
              </button>
            }
          />
        )}

        {phase === 'permission-denied' && (
          <StateScreen
            icon={MicOff}
            title="Microphone access is off"
            body="Allow the microphone in your browser settings, then try again."
          />
        )}

        {phase === 'error' && (
          <StateScreen
            icon={MicOff}
            title="Something went wrong"
            body={serverError || 'Please try again.'}
            actions={
              <button
                type="button"
                onClick={recordAnother}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white"
                style={{ backgroundColor: SAGE }}
              >
                Try again
              </button>
            }
          />
        )}
      </div>
    </div>
  );
}

export default VoiceCapture;
